import React, { useEffect, useMemo, useState, useCallback } from "react";
import styled from "styled-components";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { db } from "../firebase";
import { setLoading } from "../redux/loaderSlice";
import { calculateInterest, formatCurrencyINR } from "../utils/calculations";
import theme from "../styles/theme";
import { useNavigate } from "react-router-dom";

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 20px;
  margin-top: 60px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 {
    font-size: 28px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 15px;
    margin: 0;
  }
`;

const Button = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 16px;
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CardTitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CardValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 16px 0;
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 16px;
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 1000px;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Td = styled.td`
  padding: 10px 8px;
  font-size: 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TypeBadge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;

  ${({ $variant }) =>
    $variant === "credit" &&
    `
    background: #DCFCE7;
    color: #166534;
  `}

  ${({ $variant }) =>
    $variant === "debit" &&
    `
    background: #FEE2E2;
    color: #991B1B;
  `}
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;

  ${({ $variant }) =>
    $variant === "active" &&
    `
    background:#DCFCE7;
    color:#166534;
  `}
  ${({ $variant }) =>
    $variant === "closed" &&
    `
    background:#E5E7EB;
    color:#374151;
  `}
  ${({ $variant }) =>
    $variant === "overdue" &&
    `
    background:#FEE2E2;
    color:#991B1B;
  `}
`;

const MobileContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const MobileCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 14px;
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 500;
`;

const Val = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: right;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 15px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.spacing.radius};
  border: 1px dashed ${({ theme }) => theme.colors.border};
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : "transparent")};
  margin-bottom: -1px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

/* ================= HELPERS ================= */

const getTimestampDate = (raw) => {
  if (!raw) return null;
  if (typeof raw?.toDate === "function") return raw.toDate();
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const totalWithInterestForBill = (form) => {
  const principal = Number(form?.loanAmount) || 0;
  const interestPct = Number(form?.interest) || 0;
  return principal + (principal * interestPct) / 100;
};

const outstandingForBill = (bill) => {
  const form = bill.form || {};
  const twi = totalWithInterestForBill(form);
  if (bill.outstanding !== undefined && bill.outstanding !== null) {
    return Number(bill.outstanding);
  }
  return twi;
};

/* ================= COMPONENT ================= */

const SORT_KEYS = {
  date: "date",
  loanId: "loanId",
  customer: "customerName",
  particulars: "particulars",
  type: "entryType",
  debit: "debit",
  credit: "credit",
  balance: "runningBalance",
};

export default function Accounting() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [sort, setSort] = useState({ key: SORT_KEYS.date, dir: "desc" });

  const fetchBills = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const q = query(collection(db, "billing"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setBills(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load accounting data");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  }, [dispatch]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const kpis = useMemo(() => {
    let totalDisbursed = 0;
    let totalCollected = 0;
    let totalOutstanding = 0;
    let processingFees = 0;
    let gstCollected = 0;
    let interestAccrued = 0;

    bills.forEach((bill) => {
      const form = bill.form || {};
      const loan = Number(form.loanAmount) || 0;
      const feePct = Number(form.processingFeePercent) || 0;
      const gstPct = Number(form.gstPercent) || 0;
      const includeGst = Boolean(form.includeGst);

      totalDisbursed += loan;
      processingFees += (loan * feePct) / 100;
      if (includeGst) gstCollected += (loan * gstPct) / 100;

      interestAccrued += calculateInterest(
        form.loanAmount,
        form.interest,
        form.tenure
      );

      const payments = bill.payments || [];
      payments.forEach((p) => {
        totalCollected += Number(p.amount) || 0;
      });

      totalOutstanding += outstandingForBill(bill);
    });

    return {
      totalDisbursed,
      totalCollected,
      totalOutstanding,
      processingFees,
      gstCollected,
      interestAccrued,
    };
  }, [bills]);

  const ledgerChronological = useMemo(() => {
    const rows = [];

    bills.forEach((bill) => {
      const form = bill.form || {};
      const loanId = bill.customerId || bill.id;
      const customerName = form.customerName || "-";
      const loanAmount = Number(form.loanAmount) || 0;
      const feePct = Number(form.processingFeePercent) || 0;
      const gstPct = Number(form.gstPercent) || 0;
      const includeGst = Boolean(form.includeGst);
      const feeAmt = (loanAmount * feePct) / 100;
      const gstAmt = includeGst ? (loanAmount * gstPct) / 100 : 0;

      const created = getTimestampDate(bill.createdAt) || new Date(0);
      let seq = 0;

      const pushRow = (particulars, entryType, amount, dateOverride) => {
        seq += 1;
        const d = dateOverride || created;
        const isCredit = entryType === "Credit";
        rows.push({
          id: `${bill.id}-${seq}`,
          billId: bill.id,
          date: d,
          loanId,
          customerName,
          particulars,
          entryType,
          debit: isCredit ? null : amount,
          credit: isCredit ? amount : null,
          sortTie: seq,
        });
      };

      pushRow("Loan Disbursed", "Debit", loanAmount, created);
      // if (feeAmt > 0) {
      //   pushRow("Processing Fee Income", "Credit", feeAmt, created);
      // }
      // if (gstAmt > 0) {
      //   pushRow("GST Collected", "Credit", gstAmt, created);
      // }

      const payments = Array.isArray(bill.payments) ? bill.payments : [];
      payments.forEach((p) => {
        const pDate = p?.date ? new Date(p.date) : created;
        const amt = Number(p.amount) || 0;
        const particulars = p.isClosing ? "Loan Closure — Repayment" : "Repayment Received";
        pushRow(particulars, "Credit", amt, pDate);
      });
    });

    rows.sort((a, b) => {
      const ta = a.date.getTime();
      const tb = b.date.getTime();
      if (ta !== tb) return ta - tb;
      if (a.billId !== b.billId) return String(a.billId).localeCompare(String(b.billId));
      return a.sortTie - b.sortTie;
    });

    let running = 0;
    return rows.map((r) => {
      const amt = r.credit != null ? Number(r.credit) : Number(r.debit) || 0;
      if (r.entryType === "Credit") running += amt;
      else running -= amt;
      return { ...r, runningBalance: running };
    });
  }, [bills]);

  const sortedLedger = useMemo(() => {
    const { key, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    const copy = [...ledgerChronological];

    const cmp = (a, b) => {
      let va;
      let vb;
      switch (key) {
        case SORT_KEYS.date:
          va = a.date.getTime();
          vb = b.date.getTime();
          if (va !== vb) return (va - vb) * mult;
          return (a.billId + a.sortTie).localeCompare(b.billId + b.sortTie) * mult;

        case SORT_KEYS.loanId:
          return String(a.loanId).localeCompare(String(b.loanId)) * mult;
        case SORT_KEYS.customer:
          return String(a.customerName).localeCompare(String(b.customerName)) * mult;
        case SORT_KEYS.particulars:
          return String(a.particulars).localeCompare(String(b.particulars)) * mult;
        case SORT_KEYS.type:
          return String(a.entryType).localeCompare(String(b.entryType)) * mult;
        case SORT_KEYS.debit:
          va = a.debit != null ? Number(a.debit) : -1;
          vb = b.debit != null ? Number(b.debit) : -1;
          return (va - vb) * mult;
        case SORT_KEYS.credit:
          va = a.credit != null ? Number(a.credit) : -1;
          vb = b.credit != null ? Number(b.credit) : -1;
          return (va - vb) * mult;
        case SORT_KEYS.balance:
          return (a.runningBalance - b.runningBalance) * mult;
        default:
          return 0;
      }
    };

    copy.sort(cmp);
    return copy;
  }, [ledgerChronological, sort]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === SORT_KEYS.date ? "desc" : "asc" };
    });
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ↑" : " ↓";
  };

  const kpiItems = [
    { title: "Total Disbursed", value: formatCurrencyINR(kpis.totalDisbursed) },
    { title: "Total Collected", value: formatCurrencyINR(kpis.totalCollected) },
    // Total Collected should include payments plus processing fees and GST
    // { title: "Total Collected", value: formatCurrencyINR(kpis.totalCollected + kpis.processingFees + kpis.gstCollected) },
    { title: "Outstanding Receivables", value: formatCurrencyINR(kpis.totalOutstanding) },
    { title: "Processing Fees Income", value: formatCurrencyINR(kpis.processingFees) },
    { title: "GST Collected", value: formatCurrencyINR(kpis.gstCollected) },
    { title: "Interest Accrued", value: formatCurrencyINR(kpis.interestAccrued) },
  ];

  // Date filter state: all | today | month | year | custom
  const [dateFilter, setDateFilter] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [particularsFilter, setParticularsFilter] = useState("all");
  const [outstandingTab, setOutstandingTab] = useState("active");
  const [ledgerSearch, setLedgerSearch] = useState("");

  const outstandingSummary = useMemo(() => {
    const now = new Date();
    const rows = bills.map((bill) => {
      const form = bill.form || {};
      const loanId = bill.customerId || bill.id;
      const customerName = form.customerName || "-";
      const disbursed = Number(form.loanAmount) || 0;
      const collected = (Array.isArray(bill.payments) ? bill.payments : []).reduce(
        (sum, p) => sum + (Number(p?.amount) || 0),
        0
      );
      const outstanding = outstandingForBill(bill);
      const dueDate = form.endDate ? new Date(form.endDate) : null;

      let status = "active";
      if (outstanding <= 0) status = "closed";
      else if (dueDate && !Number.isNaN(dueDate.getTime()) && now > dueDate) status = "overdue";

      return {
        id: bill.id,
        loanId,
        customerName,
        disbursed,
        collected,
        outstanding,
        dueDate,
        status,
      };
    });

    // Show highest outstanding first
    rows.sort((a, b) => (Number(b.outstanding) || 0) - (Number(a.outstanding) || 0));
    return rows;
  }, [bills]);

  const filteredOutstandingSummary = useMemo(() => {
    return outstandingSummary.filter(r => {
      if (outstandingTab === "active") return r.status === "active" || r.status === "overdue";
      if (outstandingTab === "closed") return r.status === "closed";
      return true;
    });
  }, [outstandingSummary, outstandingTab]);

  const parseYMD = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const filteredLedger = useMemo(() => {
    if (!sortedLedger || sortedLedger.length === 0) return [];
    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    let start = null;
    let end = null;

    if (dateFilter === "today") {
      start = startOfToday;
      end = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1);
    } else if (dateFilter === "month") {
      start = startOfMonth;
      end = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, -1);
    } else if (dateFilter === "year") {
      start = startOfYear;
      end = new Date(now.getFullYear() + 1, 0, 1, 0, 0, 0, -1);
    } else if (dateFilter === "custom") {
      start = parseYMD(customStart);
      end = parseYMD(customEnd);
      if (start && !end) end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
    }

    const dateFiltered = (!start && !end)
      ? sortedLedger
      : sortedLedger.filter((r) => {
        const t = r.date instanceof Date ? r.date.getTime() : new Date(r.date).getTime();
        if (start && t < start.getTime()) return false;
        if (end && t > end.getTime()) return false;
        return true;
      });

    let result = dateFiltered;
    if (particularsFilter && particularsFilter !== "all") {
      result = result.filter((r) => String(r.particulars) === String(particularsFilter));
    }

    if (ledgerSearch.trim()) {
      const q = ledgerSearch.toLowerCase();
      result = result.filter(r =>
        String(r.loanId || "").toLowerCase().includes(q) ||
        String(r.customerName || "").toLowerCase().includes(q)
      );
    }

    return result;
  }, [sortedLedger, dateFilter, customStart, customEnd, particularsFilter, ledgerSearch]);

  const particularsOptions = useMemo(() => {
    const set = new Set();
    sortedLedger.forEach((r) => {
      if (r?.particulars) set.add(String(r.particulars));
    });
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [sortedLedger]);

  const exportLedgerToCsv = (rows) => {
    if (!rows || rows.length === 0) return toast.error("Nothing to export");
    const headers = ["Date", "Loan ID", "Customer", "Particulars", "Type", "Debit", "Credit", "Running Balance"];
    const escape = (s) => {
      if (s == null) return "";
      const str = String(s);
      if (str.includes('"') || str.includes(",") || str.includes("\n")) {
        return "\"" + str.replace(/"/g, '""') + "\"";
      }
      return str;
    };

    const lines = rows.map((r) => [
      formatDisplayDate(r.date),
      escape(r.loanId),
      escape(r.customerName),
      escape(r.particulars),
      r.entryType,
      r.debit != null ? Number(r.debit).toFixed(2) : "",
      r.credit != null ? Number(r.credit).toFixed(2) : "",
      Number(r.runningBalance || 0).toFixed(2),
    ].join(","));

    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `accounting_ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Wrapper>
      <HeaderSection>
        <h1>Accounting</h1>
        {/* <p>Day book and financial summary derived from billing records.</p> */}

        <Button onClick={() => navigate("/inventory")}>Investment</Button>
      </HeaderSection>

      <Cards>
        {kpiItems.map((item) => (
          <Card key={item.title}>
            <CardTitle>{item.title}</CardTitle>
            <CardValue>{item.value}</CardValue>
          </Card>
        ))}
      </Cards>

      <SectionTitle>Outstanding Summary</SectionTitle>

      <TabsContainer>
        <TabButton
          $active={outstandingTab === "active"}
          onClick={() => setOutstandingTab("active")}
        >
          Active Loans
        </TabButton>
        <TabButton
          $active={outstandingTab === "closed"}
          onClick={() => setOutstandingTab("closed")}
        >
          Closed Loans
        </TabButton>
      </TabsContainer>

      {filteredOutstandingSummary.length === 0 ? (
        <EmptyState>No {outstandingTab} loans found.</EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <Th>Loan ID</Th>
                  <Th>Customer</Th>
                  <Th style={{ textAlign: "right" }}>Disbursed</Th>
                  <Th style={{ textAlign: "right" }}>Collected</Th>
                  <Th style={{ textAlign: "right" }}>Outstanding</Th>
                  <Th>Due Date</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {filteredOutstandingSummary.map((r) => (
                  <tr key={r.id}>
                    <Td>{r.loanId}</Td>
                    <Td>{r.customerName}</Td>
                    <Td style={{ textAlign: "right" }}>{formatCurrencyINR(r.disbursed)}</Td>
                    <Td style={{ textAlign: "right" }}>{formatCurrencyINR(r.collected)}</Td>
                    <Td style={{ textAlign: "right", fontWeight: 700 }}>{formatCurrencyINR(r.outstanding)}</Td>
                    <Td>{r.dueDate ? formatDisplayDate(r.dueDate) : "-"}</Td>
                    <Td>
                      <StatusBadge $variant={r.status}>{r.status}</StatusBadge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>

          <MobileContainer>
            {filteredOutstandingSummary.map((r) => (
              <MobileCard key={r.id}>
                <Row>
                  <Label>Loan ID</Label>
                  <Val>{r.loanId}</Val>
                </Row>
                <Row>
                  <Label>Customer</Label>
                  <Val>{r.customerName}</Val>
                </Row>
                <Row>
                  <Label>Disbursed</Label>
                  <Val>{formatCurrencyINR(r.disbursed)}</Val>
                </Row>
                <Row>
                  <Label>Collected</Label>
                  <Val>{formatCurrencyINR(r.collected)}</Val>
                </Row>
                <Row>
                  <Label>Outstanding</Label>
                  <Val>{formatCurrencyINR(r.outstanding)}</Val>
                </Row>
                <Row>
                  <Label>Due Date</Label>
                  <Val>{r.dueDate ? formatDisplayDate(r.dueDate) : "-"}</Val>
                </Row>
                <Row>
                  <Label>Status</Label>
                  <Val>
                    <StatusBadge $variant={r.status}>{r.status}</StatusBadge>
                  </Val>
                </Row>
              </MobileCard>
            ))}
          </MobileContainer>
        </>
      )}

      <SectionTitle style={{ marginTop: 20 }}>Transactions Ledger</SectionTitle>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "#6b7280", marginRight: 6 }}>Filter:</label>
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB", background: "#fff" }}>
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
            <option value="custom">Custom</option>
          </select>
          {dateFilter === "custom" && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB" }} />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB" }} />
            </>
          )}

          <select
            value={particularsFilter}
            onChange={(e) => setParticularsFilter(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB", background: "#fff" }}
            title="Filter by Particulars"
          >
            {particularsOptions.map((p) => (
              <option key={p} value={p}>
                {p === "all" ? "All Particulars" : p}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search Loan ID / Customer"
            value={ledgerSearch}
            onChange={(e) => setLedgerSearch(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB", minWidth: "200px" }}
          />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => exportLedgerToCsv(filteredLedger)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #D1D5DB", background: `${theme.colors.accent}`, color: `${theme.colors.textPrimary}`, cursor: "pointer", fontWeight: 600 }}>
            Export CSV
          </button>
        </div>
      </div>

      {filteredLedger.length === 0 ? (
        <EmptyState>No billing transactions yet. Saved loans will appear here.</EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th onClick={() => toggleSort(SORT_KEYS.date)}>
                    Date{sortIndicator(SORT_KEYS.date)}
                  </Th>
                  {/* Voucher column removed */}
                  <Th onClick={() => toggleSort(SORT_KEYS.loanId)}>
                    Loan ID{sortIndicator(SORT_KEYS.loanId)}
                  </Th>
                  <Th onClick={() => toggleSort(SORT_KEYS.customer)}>
                    Customer{sortIndicator(SORT_KEYS.customer)}
                  </Th>
                  <Th onClick={() => toggleSort(SORT_KEYS.particulars)}>
                    Particulars{sortIndicator(SORT_KEYS.particulars)}
                  </Th>
                  <Th onClick={() => toggleSort(SORT_KEYS.type)}>
                    Type{sortIndicator(SORT_KEYS.type)}
                  </Th>
                  <Th onClick={() => toggleSort(SORT_KEYS.debit)} style={{ textAlign: "right" }}>
                    Debit{sortIndicator(SORT_KEYS.debit)}
                  </Th>
                  <Th onClick={() => toggleSort(SORT_KEYS.credit)} style={{ textAlign: "right" }}>
                    Credit{sortIndicator(SORT_KEYS.credit)}
                  </Th>
                  <Th onClick={() => toggleSort(SORT_KEYS.balance)} style={{ textAlign: "right" }}>
                    Running balance{sortIndicator(SORT_KEYS.balance)}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {filteredLedger.map((r) => (
                  <tr key={r.id}>
                    <Td>{formatDisplayDate(r.date)}</Td>
                    {/* voucher removed */}
                    <Td>{r.loanId}</Td>
                    <Td>{r.customerName}</Td>
                    <Td>{r.particulars}</Td>
                    <Td>
                      <TypeBadge $variant={r.entryType === "Credit" ? "credit" : "debit"}>
                        {r.entryType}
                      </TypeBadge>
                    </Td>
                    <Td style={{ textAlign: "right" }}>
                      {r.debit != null ? formatCurrencyINR(r.debit) : "—"}
                    </Td>
                    <Td style={{ textAlign: "right" }}>
                      {r.credit != null ? formatCurrencyINR(r.credit) : "—"}
                    </Td>
                    <Td style={{ textAlign: "right", fontWeight: 600 }}>
                      {formatCurrencyINR(r.runningBalance)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>

          <MobileContainer>
            {filteredLedger.map((r) => (
              <MobileCard key={r.id}>
                <Row>
                  <Label>Date</Label>
                  <Val>{formatDisplayDate(r.date)}</Val>
                </Row>
                {/* Voucher removed */}
                <Row>
                  <Label>Loan / Customer</Label>
                  <Val>
                    {r.loanId}
                    <br />
                    <span style={{ fontWeight: 500, fontSize: 12 }}>{r.customerName}</span>
                  </Val>
                </Row>
                <Row>
                  <Label>Particulars</Label>
                  <Val style={{ maxWidth: "55%" }}>{r.particulars}</Val>
                </Row>
                <Row>
                  <Label>Type</Label>
                  <Val>
                    <TypeBadge $variant={r.entryType === "Credit" ? "credit" : "debit"}>
                      {r.entryType}
                    </TypeBadge>
                  </Val>
                </Row>
                <Row>
                  <Label>Debit</Label>
                  <Val>{r.debit != null ? formatCurrencyINR(r.debit) : "—"}</Val>
                </Row>
                <Row>
                  <Label>Credit</Label>
                  <Val>{r.credit != null ? formatCurrencyINR(r.credit) : "—"}</Val>
                </Row>
                <Row>
                  <Label>Running balance</Label>
                  <Val>{formatCurrencyINR(r.runningBalance)}</Val>
                </Row>
              </MobileCard>
            ))}
          </MobileContainer>
        </>
      )}
    </Wrapper>
  );
}
